// Cohort management surface. Instructors / admins can create cohorts, add learners by user_id,
// drill into per-member MCQ pass + run stats + weak-area chips, remove members, delete cohorts,
// and export the cohort as XLSX (4 sheets) or PDF (summary report).

import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';

interface CohortRow {
  id: string;
  name: string;
  region: string;
  cohort_code: string | null;
  created_at: number;
  member_count: number;
}

interface Member {
  id: string;
  display_name: string;
  email: string | null;
  role: string;
  joined_at: number;
  mcq_best_pct: number | null;
  mcq_attempts_count: number;
  runs_total: number;
  runs_rosc: number;
  run_avg_score: number;
  avg_time_to_first_shock_s: number | null;
  avg_ccf_pct: number | null;
  last_run_at: number | null;
  weak_areas: Array<{ category: string; count: number }>;
  topic_breakdown: Array<{ topic: string; correct: number; total: number; pass_pct: number }>;
  runs_by_difficulty: Record<string, number>;
}

interface CohortDetail { cohort: CohortRow; members: Member[]; }

export default function Cohorts() {
  const [cohorts, setCohorts] = useState<CohortRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<CohortDetail | null>(null);
  const [newName, setNewName] = useState('');
  const [allUsers, setAllUsers] = useState<any[] | null>(null);
  const [addQuery, setAddQuery] = useState('');

  function refreshList() { api.adminCohorts().then((r) => setCohorts(r.cohorts)); }
  function refreshDetail() {
    if (!selected) return;
    setDetail(null);
    api.adminCohortDetail(selected)
      .then((r) => setDetail(r as any))
      .catch((e) => { console.error('Failed to load cohort detail', e); alert(`Failed to load cohort: ${e.message || e}`); });
  }

  useEffect(() => { refreshList(); api.adminUsers().then((r) => setAllUsers(r.users)).catch(() => setAllUsers([])); }, []);
  useEffect(() => { if (selected) refreshDetail(); else setDetail(null); }, [selected]);

  async function createCohort() {
    if (newName.trim().length < 2) return;
    const r = await api.adminCohortCreate(newName.trim());
    setNewName('');
    refreshList();
    setSelected(r.id);
  }

  async function addMember(user_id: string) {
    if (!selected) return;
    await api.adminCohortAddMember(selected, user_id);
    setAddQuery('');
    refreshDetail();
    refreshList();
  }

  async function removeMember(user_id: string) {
    if (!selected) return;
    if (!confirm('Remove this member from the cohort?')) return;
    await api.adminCohortRemoveMember(selected, user_id);
    refreshDetail();
    refreshList();
  }

  async function deleteCohort() {
    if (!selected || !detail) return;
    const msg = detail.members.length > 0
      ? `Delete cohort "${detail.cohort.name}" and remove all ${detail.members.length} members? The members' run/MCQ history is NOT deleted — only their cohort association.`
      : `Delete the empty cohort "${detail.cohort.name}"?`;
    if (!confirm(msg)) return;
    try {
      await api.adminCohortDelete(selected);
      setSelected(null);
      setDetail(null);
      refreshList();
    } catch (e: any) {
      alert(`Failed to delete: ${e.message || e}`);
    }
  }

  const memberIds = useMemo(() => new Set((detail?.members || []).map((m) => m.id)), [detail]);
  const filtered = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    if (!q || !allUsers) return [];
    return allUsers
      .filter((u: any) => !memberIds.has(u.id) && (u.display_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.id.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [addQuery, allUsers, memberIds]);

  // Cohort-level aggregates for the summary card
  const agg = useMemo(() => {
    if (!detail || detail.members.length === 0) return null;
    const m = detail.members;
    const avg = (xs: number[]) => xs.length > 0 ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
    const mcqPassed = m.filter((x) => (x.mcq_best_pct ?? 0) >= 75).length;
    const totalRuns = m.reduce((s, x) => s + x.runs_total, 0);
    const totalRosc = m.reduce((s, x) => s + x.runs_rosc, 0);
    const avgScore = avg(m.map((x) => x.run_avg_score).filter((v) => v > 0));
    const avgTtfs = avg(m.map((x) => x.avg_time_to_first_shock_s ?? 0).filter((v) => v > 0));
    const avgCcf = avg(m.map((x) => x.avg_ccf_pct ?? 0).filter((v) => v > 0));
    // Cohort-wide weak-area histogram
    const weakAgg = new Map<string, number>();
    for (const x of m) for (const w of x.weak_areas) weakAgg.set(w.category, (weakAgg.get(w.category) || 0) + w.count);
    const weakTop = Array.from(weakAgg.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5);
    return { mcqPassed, totalRuns, totalRosc, avgScore, avgTtfs, avgCcf, weakTop };
  }, [detail]);

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-1">Cohorts</h1>
      <p className="text-mono-white/50 text-sm mb-4">Group learners for cohort-level reporting. MCQ-pass status, run scores, timing metrics and weak-area patterns surface per-member. Export as XLSX (multi-sheet) or PDF (one-page summary).</p>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left column: cohort list + create */}
        <div className="lg:col-span-1 space-y-3">
          <div className="panel p-3">
            <div className="text-xs uppercase tracking-widest text-mono-white/40 mb-2">New cohort</div>
            <div className="flex gap-2">
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. NHS-Trust Q1 2026" className="flex-1 bg-ink-800 border border-ink-800 rounded px-2 py-2 text-mono-white focus:border-brand-600 focus:outline-none text-sm" />
              <button onClick={createCohort} disabled={newName.trim().length < 2} className="btn-primary text-sm disabled:opacity-30">Create</button>
            </div>
          </div>

          <div className="space-y-2">
            {cohorts == null && <p className="text-mono-white/60 text-sm">Loading…</p>}
            {cohorts && cohorts.length === 0 && <p className="text-mono-white/60 panel p-3 text-sm">No cohorts yet — create one above.</p>}
            {cohorts && cohorts.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                className={`w-full text-left panel p-3 transition-colors ${selected === c.id ? 'border-brand-600 bg-brand-600/5' : 'hover:border-brand-600/60 hover:bg-ink-800/50'}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <div className="font-medium flex items-center gap-1">
                      {c.name}
                      <span className="text-mono-white/30 text-xs">{selected === c.id ? '↘' : '→'}</span>
                    </div>
                    <div className="text-xs text-mono-white/40 mt-1">{c.region} · {new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className="chip-off text-[10px]">{c.member_count} member{c.member_count === 1 ? '' : 's'}</span>
                </div>
                {c.cohort_code && <div className="text-[10px] text-mono-white/40 font-mono mt-1">code · {c.cohort_code}</div>}
                {selected !== c.id && c.member_count === 0 && (
                  <div className="text-[10px] text-brand-600 mt-1 italic">Click to manage members →</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Right column: cohort detail */}
        <div className="lg:col-span-2">
          {!detail && <div className="panel p-6 text-center text-mono-white/50 text-sm">Select a cohort to view members + analytics.</div>}
          {detail && (
            <>
              <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                <div>
                  <h2 className="text-xl font-semibold">{detail.cohort.name}</h2>
                  <p className="text-xs text-mono-white/40 mt-1">{detail.members.length} member{detail.members.length === 1 ? '' : 's'} · {detail.cohort.region} · created {new Date(detail.cohort.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a href={api.adminCohortExportXlsxUrl(detail.cohort.id)} download className="btn-ghost text-sm" title="Download as XLSX (Members, Runs, Topics)">⤓ XLSX</a>
                  <a href={api.adminCohortReportPdfUrl(detail.cohort.id)} download className="btn-ghost text-sm" title="One-page PDF report">⤓ PDF</a>
                  <button onClick={deleteCohort} className="btn-ghost text-sm text-alarm-red hover:bg-alarm-red/15">⌫ Delete cohort</button>
                </div>
              </div>

              {/* Cohort-level summary card */}
              {agg && (
                <div className="panel p-4 mb-4 bg-ink-800/40">
                  <div className="text-xs uppercase tracking-widest text-mono-white/40 mb-3">Cohort summary</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-center">
                    <Stat label="MCQ passed"        value={`${agg.mcqPassed}/${detail.members.length}`} accent={agg.mcqPassed === detail.members.length ? 'text-mono-green' : 'text-alarm-amber'} />
                    <Stat label="Runs"              value={String(agg.totalRuns)} />
                    <Stat label="ROSC"              value={String(agg.totalRosc)} accent="text-mono-green" />
                    <Stat label="Avg score"         value={agg.avgScore > 0 ? agg.avgScore.toFixed(0) : '—'} />
                    <Stat label="Avg TTFS"          value={agg.avgTtfs > 0 ? `${agg.avgTtfs.toFixed(0)} s` : '—'} />
                    <Stat label="Avg CCF"           value={agg.avgCcf > 0 ? `${agg.avgCcf.toFixed(1)}%` : '—'} />
                  </div>
                  {agg.weakTop.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-ink-800">
                      <div className="text-xs uppercase tracking-widest text-mono-white/40 mb-2">Cohort weak areas (critical + major)</div>
                      <div className="flex flex-wrap gap-1">
                        {agg.weakTop.map((w) => (
                          <span key={w[0]} className="chip bg-alarm-red/15 text-alarm-red text-[11px]">{w[0]} · {w[1]}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Add a member */}
              <div className="panel p-3 mb-4">
                <label className="text-xs">
                  <span className="text-mono-white/40 uppercase tracking-widest">Add a learner</span>
                  <input type="text" value={addQuery} onChange={(e) => setAddQuery(e.target.value)} placeholder="Search by name, email or id…" className="mt-1 w-full bg-ink-800 border border-ink-800 rounded px-2 py-2 text-mono-white focus:border-brand-600 focus:outline-none text-sm" />
                </label>
                {filtered.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {filtered.map((u: any) => (
                      <button key={u.id} onClick={() => addMember(u.id)} className="w-full flex justify-between items-center text-left bg-ink-800 hover:bg-ink-950 rounded px-2 py-1.5 text-sm">
                        <span>{u.display_name} <span className="text-mono-white/40 text-xs">· {u.email || u.id.slice(0, 12)}</span></span>
                        <span className="text-mono-green text-xs">+ add</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Members table */}
              {detail.members.length === 0 && <p className="text-mono-white/60 panel p-4 text-sm">No members in this cohort yet — search above to add learners.</p>}
              {detail.members.length > 0 && (
                <div className="panel overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-mono-white/40 bg-ink-800/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Learner</th>
                        <th className="text-right px-3 py-2 font-medium" title="Best MCQ score">MCQ</th>
                        <th className="text-right px-3 py-2 font-medium">Runs</th>
                        <th className="text-right px-3 py-2 font-medium">ROSC</th>
                        <th className="text-right px-3 py-2 font-medium" title="Average run score">Avg</th>
                        <th className="text-right px-3 py-2 font-medium" title="Average time-to-first-shock">TTFS</th>
                        <th className="text-right px-3 py-2 font-medium" title="Average chest-compression fraction">CCF</th>
                        <th className="text-left px-3 py-2 font-medium">Last run</th>
                        <th className="text-left px-3 py-2 font-medium">Weak areas</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {detail.members.map((m) => (
                        <tr key={m.id} className="border-t border-ink-800 hover:bg-ink-800/30">
                          <td className="px-3 py-2">
                            <div className="font-medium">{m.display_name}</div>
                            <div className="text-[10px] text-mono-white/40">
                              {m.email || m.id.slice(0, 12)} · <span className="uppercase">{m.role}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {m.mcq_best_pct != null ? (
                              <span className={`font-mono tabular-nums ${m.mcq_best_pct >= 75 ? 'text-mono-green' : 'text-alarm-amber'}`}>{m.mcq_best_pct}%</span>
                            ) : <span className="text-mono-white/30 text-xs">—</span>}
                            {m.mcq_attempts_count > 0 && (
                              <div className="text-[10px] text-mono-white/30">×{m.mcq_attempts_count}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{m.runs_total}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-mono-green">{m.runs_rosc}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{m.run_avg_score || '—'}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{m.avg_time_to_first_shock_s != null ? `${m.avg_time_to_first_shock_s}s` : '—'}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{m.avg_ccf_pct != null ? `${m.avg_ccf_pct}%` : '—'}</td>
                          <td className="px-3 py-2 text-xs">{m.last_run_at ? new Date(m.last_run_at).toLocaleDateString() : <span className="text-mono-white/30">never</span>}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {m.weak_areas.length === 0 && <span className="text-mono-white/30 text-xs">—</span>}
                              {m.weak_areas.map((w) => (
                                <span key={w.category} className="chip bg-alarm-red/15 text-alarm-red text-[10px]">{w.category} · {w.count}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => removeMember(m.id)} className="text-mono-white/30 hover:text-alarm-red text-xs">remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div>
      <div className="text-[10px] text-mono-white/40 uppercase tracking-widest">{label}</div>
      <div className={`text-lg font-mono tabular-nums mt-0.5 ${accent || 'text-mono-white'}`}>{value}</div>
    </div>
  );
}
